/**
 * A node's connection setting is either a connection id or a `@role`.
 *
 * Roles resolve **at run time** on the server, which is what lets one validated
 * pipeline run in staging against staging and in production against production.
 * The canvas, though, calls the metadata endpoints *now* — to list a vault's
 * objects, describe a field, offer a document type — and those take a
 * connection id. Handed a role they answered "no connection '@rim_sandbox'",
 * so every inspector on a role-using node showed empty pickers and an error.
 *
 * This resolves the role the same way the run does, through the bindings the
 * deployment already publishes, so the inspectors work on exactly the pipelines
 * roles exist for.
 */

import { useEffect, useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnectionBindings } from "@/shared/api/integration";

/** `@name` — the same marker the engine uses, and the reason a role can never
 *  be confused with a ULID. */
export const ROLE_PREFIX = "@";

export function isConnectionRole(ref: string | undefined | null): boolean {
  return typeof ref === "string" && ref.startsWith(ROLE_PREFIX);
}

export function connectionRoleName(ref: string): string {
  return ref.slice(ROLE_PREFIX.length);
}

export interface ResolvedConnection {
  /** The connection id to call metadata with — empty while a role is resolving,
   *  or if it is bound to nothing here. */
  connectionId: string;
  /** The role this came from, if it did. */
  role: string | null;
  /** True once a role lookup has finished, so a picker can tell "still loading"
   *  from "bound to nothing". */
  settled: boolean;
}

/**
 * Resolve a node's connection setting to an id the metadata endpoints accept.
 *
 * A plain id passes straight through and costs nothing. Only a `@role` fetches,
 * and the bindings list is small and readable by anyone who may see connections.
 */
export function useConnectionRef(ref: string | undefined | null): ResolvedConnection {
  const getToken = useAccessToken();
  const value = String(ref ?? "");
  const role = isConnectionRole(value) ? connectionRoleName(value) : null;
  const [bound, setBound] = useState<string>("");
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!role) {
      setBound("");
      setSettled(true);
      return;
    }
    let cancelled = false;
    setSettled(false);
    void (async () => {
      try {
        const items = (await listConnectionBindings(await getToken())).items;
        if (!cancelled) setBound(items.find((b) => b.role === role)?.connection_id ?? "");
      } catch {
        // Silent on purpose: a failed lookup leaves the pickers empty, which the
        // inspector already explains. A toast here would fire once per inspector
        // on a page with several role-using nodes.
        if (!cancelled) setBound("");
      } finally {
        if (!cancelled) setSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, role]);

  return { connectionId: role ? bound : value, role, settled };
}
